import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import { GetModuleAssessmentSummary } from '../types';

export const getModuleAssessmentSummary = (
  moduleId: string,
  assessmentId: string
): Promise<GetModuleAssessmentSummary.Root> => {
  return axios.get(
    '/assessment/v1/users/reports/get-module-assessment-summary',
    {
      params: {
        moduleId,
        assessmentId,
      },
    }
  );
};

const generateModuleAssessmentAttempt = (
  attemptNumber: number
): GetModuleAssessmentSummary.Attempt => {
  const totalQuestions = faker.number.int({ min: 10, max: 20 });
  const correctQuestions = faker.number.int({ min: 0, max: totalQuestions });
  const accuracy = Math.round((correctQuestions / totalQuestions) * 100);

  return {
    attemptNumber,
    date: faker.date.recent({ days: 30 }).getTime().toString().slice(0, -3),
    marksObtained: faker.number.int({ min: 0, max: 100 }),
    coinsEarned: faker.number.int({ min: 0, max: 10 }),
    accuracy,
    proficiency: faker.number.float({ min: 0, max: 1, fractionDigits: 2 }),
    speedPercentile: faker.number.float({
      min: 0,
      max: 100,
      fractionDigits: 4,
    }),
    timeSpentSec: faker.number.int({ min: 10, max: 600 }),
    totalQuestions,
    correctQuestions,
    testType: 'ASSESSMENT',
    sessionId: faker.string.uuid(),
  };
};

const generateModuleAssessmentSummary = (
  assessmentId: string
): GetModuleAssessmentSummary.Root => {
  const numberOfAttempts = faker.number.int({ min: 1, max: 4 });
  const attempts = Array.from({ length: numberOfAttempts }, (_, index) =>
    generateModuleAssessmentAttempt(index + 1)
  );

  return {
    status: 'success',
    responseBody: {
      assessmentId,
      assessmentName: faker.helpers.arrayElement([
        'The True AI, ML Introduction',
        'Mathematics Foundation Assessment',
        'Advanced Problem Solving',
        'Number Theory Basics',
      ]),
      viewType: faker.helpers.arrayElement(['POST', 'PRE']),
      assessmentImageUrl: 'https://picsum.photos/200',
      reAttemptAllowed: faker.datatype.boolean(),
      attempts,
    },
    errorCode: 0,
    errorMessage: null,
  };
};

export const getModuleAssessmentSummaryMockHandler = http.get(
  `${env.API_URL}/assessment/v1/users/reports/get-module-assessment-summary`,
  async ({
    request,
  }): Promise<HttpResponse<GetModuleAssessmentSummary.Root>> => {
    await networkDelay();

    const url = new URL(request.url);
    const assessmentId =
      url.searchParams.get('assessmentId') ??
      'e8b61c5b-d5c4-47a2-8e02-5c34ed3aeb01';

    const mockResponse = generateModuleAssessmentSummary(assessmentId);

    return HttpResponse.json(mockResponse);
  }
);
